import React from 'react';
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { getCookie } from '@/utils/cookie';
import { translateByPattern } from './patterns';
import { DEFAULT_LOCALE, DISPLAY_TRANSLATIONS, SUPPORTED_LOCALES, type AppLocale } from './translations';

const HAS_KOREAN = /[가-힣]/;

export function isSupportedLocale(value: string | null | undefined): value is AppLocale {
	return SUPPORTED_LOCALES.includes(value as AppLocale);
}

export function getLocaleFromCookie(): AppLocale {
	if (typeof document === 'undefined') return DEFAULT_LOCALE;

	const locale = getCookie('locale');
	return isSupportedLocale(locale) ? locale : DEFAULT_LOCALE;
}

export function getCurrentLocale(): AppLocale {
	const language = i18n.resolvedLanguage || i18n.language;
	return isSupportedLocale(language) ? language : DEFAULT_LOCALE;
}

i18n.use(initReactI18next).init({
	lng: getLocaleFromCookie(),
	fallbackLng: DEFAULT_LOCALE,
	supportedLngs: SUPPORTED_LOCALES,
	resources: {
		ko: { translation: {} },
		en: { translation: DISPLAY_TRANSLATIONS.en },
		ja: { translation: DISPLAY_TRANSLATIONS.ja },
	},
	keySeparator: false,
	nsSeparator: false,
	interpolation: {
		escapeValue: false,
	},
	returnEmptyString: false,
	returnNull: false,
});

function applyInterpolation(value: string, params?: Record<string, unknown>): string {
	if (!params) return value;

	return value.replace(/{{\s*(\w+)\s*}}/g, (_, key: string) => String(params[key] ?? ''));
}

function translateCore(value: string, locale: AppLocale): string {
	if (!HAS_KOREAN.test(value)) return value;

	const direct = i18n.getResource(locale, 'translation', value);
	if (typeof direct === 'string') return direct;

	const patternValue = translateByPattern(value, locale);
	return patternValue ?? value;
}

export function translateText(value: string, params?: Record<string, unknown>): string {
	const source = applyInterpolation(value, params);
	const locale = getCurrentLocale();

	if (locale === DEFAULT_LOCALE) return source;
	return translateCore(source, locale);
}

export const t = translateText;

export function translateNode(node: React.ReactNode): React.ReactNode {
	if (typeof node === 'string') return translateText(node);

	if (Array.isArray(node)) {
		return node.map((item, index) => React.createElement(React.Fragment, { key: index }, translateNode(item)));
	}

	if (React.isValidElement<{ children?: React.ReactNode }>(node)) {
		const children = node.props.children;
		if (children == null) return node;

		return React.cloneElement(node, undefined, translateNode(children));
	}

	return node;
}

export { i18n };
export type { AppLocale };
